import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  message,
  Tag,
  Popconfirm,
  Alert,
  Row,
  Col,
  Statistic,
  Switch
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  KeyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const { Option } = Select;

const OperatorCodes = () => {
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [operatorCodes, setOperatorCodes] = useState([]);
  const [classes, setClasses] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [statistics, setStatistics] = useState({
    total: 0,
    active: 0,
    inactive: 0
  });

  // 操作员类型选项
  const operatorTypes = [
    { value: 'student', label: '学生', color: 'blue' },
    { value: 'monitor', label: '班长', color: 'green' },
    { value: 'duty', label: '值日生', color: 'orange' },
    { value: 'teacher', label: '教师', color: 'red' },
  ];

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    if (selectedClass) {
      fetchOperatorCodes(selectedClass);
    }
  }, [selectedClass]);

  // 获取班级列表
  const fetchClasses = async () => {
    try {
      const response = await api.get('/classes');
      if (response.data.success) {
        const classList = response.data.data || [];
        setClasses(classList);
        // 默认选中第一个班级
        if (classList.length > 0 && !selectedClass) {
          setSelectedClass(classList[0].id);
        }
      }
    } catch (error) {
      console.error('获取班级列表失败:', error);
      message.error('获取班级列表失败');
    }
  };

  // 获取操作码列表
  const fetchOperatorCodes = async (classId) => {
    if (!classId) {
      setOperatorCodes([]);
      return;
    }
    setLoading(true);
    try {
      const response = await api.get(`/operator-codes/${classId}`);
      if (response.data.success) {
        const codes = response.data.data || [];
        setOperatorCodes(codes);
        
        // 计算统计数据
        setStatistics({
          total: codes.length,
          active: codes.filter(c => c.is_active).length,
          inactive: codes.filter(c => !c.is_active).length
        });
      }
    } catch (error) {
      console.error('获取操作码失败:', error);
      message.error('获取操作码失败');
      setOperatorCodes([]);
    } finally {
      setLoading(false);
    }
  };

  // 打开模态框
  const openModal = (code = null) => {
    setEditingCode(code);
    setModalVisible(true);
    
    if (code) {
      form.setFieldsValue(code);
    } else {
      form.resetFields();
      form.setFieldsValue({
        class_id: selectedClass,
        operator_type: 'student',
        is_active: true
      });
    }
  };

  // 关闭模态框
  const closeModal = () => {
    setModalVisible(false);
    setEditingCode(null);
    form.resetFields();
  };

  // 提交表单
  const handleSubmit = async (values) => {
    try {
      if (editingCode) {
        await api.put(`/operator-codes/${editingCode.id}`, values);
        message.success('操作码更新成功');
      } else {
        await api.post('/operator-codes', values);
        message.success('操作码创建成功');
      }
      closeModal();
      fetchOperatorCodes(values.class_id);
    } catch (error) {
      message.error(error.response?.data?.message || (editingCode ? '更新失败' : '创建失败'));
      console.error('提交失败:', error);
    }
  };

  // 删除操作码
  const handleDelete = async (id, classId) => {
    try {
      await api.delete(`/operator-codes/${id}`);
      message.success('删除成功');
      fetchOperatorCodes(classId);
    } catch (error) {
      message.error('删除失败');
      console.error('删除失败:', error);
    }
  };

  // 切换状态
  const handleToggleStatus = async (id, isActive, classId) => {
    try {
      await api.put(`/operator-codes/${id}`, { is_active: !isActive });
      message.success(`已${isActive ? '停用' : '启用'}操作码`);
      fetchOperatorCodes(classId);
    } catch (error) {
      message.error('状态切换失败');
      console.error('状态切换失败:', error);
    }
  };

  // 获取操作员类型标签
  const getOperatorTypeTag = (type) => {
    const typeObj = operatorTypes.find(t => t.value === type);
    if (!typeObj) return <Tag>{type}</Tag>;
    return <Tag color={typeObj.color}>{typeObj.label}</Tag>;
  };

  const columns = [
    {
      title: '操作码',
      dataIndex: 'code',
      key: 'code',
      width: 120,
      render: (text) => (
        <Tag color="purple" icon={<KeyOutlined />} style={{ fontSize: 13, padding: '4px 8px' }}>
          {text}
        </Tag>
      )
    },
    {
      title: '操作人姓名',
      dataIndex: 'operator_name',
      key: 'operator_name',
      width: 120
    },
    {
      title: '操作员类型',
      dataIndex: 'operator_type',
      key: 'operator_type',
      width: 100,
      render: (type) => getOperatorTypeTag(type)
    },
    {
      title: '所属班级',
      dataIndex: 'class_name',
      key: 'class_name',
      width: 120
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text) => text || '-'
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          checkedChildren="启用"
          unCheckedChildren="停用"
          onChange={() => handleToggleStatus(record.id, isActive, record.class_id)}
        />
      )
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (text) => new Date(text).toLocaleString()
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个操作码吗？"
            onConfirm={() => handleDelete(record.id, record.class_id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="操作码总数"
              value={statistics.total}
              prefix={<KeyOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="启用中"
              value={statistics.active}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="已停用"
              value={statistics.inactive}
              prefix={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      <Card title="操作码管理">
        <Alert
          message="操作码使用说明"
          description="操作码用于桌面客户端的各项功能（考勤、作业、违纪、积分等）。教师可以为班级设置不同的操作码，分配给值日生、班长等学生使用。注意：教师和管理员在后台操作时不需要操作码。"
          type="info"
          style={{ marginBottom: 16 }}
          showIcon
        />

        <Space style={{ marginBottom: '16px' }}>
          <Select
            placeholder="选择班级查看操作码"
            style={{ width: 200 }}
            onChange={(value) => setSelectedClass(value)}
            value={selectedClass}
          >
            {classes.map(cls => (
              <Option key={cls.id} value={cls.id}>
                {cls.name}
              </Option>
            ))}
          </Select>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => openModal()}
            disabled={!selectedClass}
          >
            新增操作码
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={operatorCodes}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showTotal: (total) => `共 ${total} 条记录`
          }}
          locale={{ emptyText: selectedClass ? '暂无操作码' : '请先选择班级' }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 操作码模态框 */}
      <Modal
        title={editingCode ? '编辑操作码' : '新增操作码'}
        open={modalVisible}
        onCancel={closeModal}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="class_id"
            label="班级"
            rules={[{ required: true, message: '请选择班级' }]}
          >
            <Select placeholder="选择班级" disabled={!!editingCode}>
              {classes.map(cls => (
                <Option key={cls.id} value={cls.id}>
                  {cls.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="code"
                label="操作码"
                rules={[
                  { required: true, message: '请输入操作码' },
                  { pattern: /^\d+$/, message: '操作码只能包含数字' },
                  { min: 3, max: 6, message: '操作码长度为3-6位' }
                ]}
              >
                <Input placeholder="例：222" maxLength={6} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="operator_type"
                label="操作员类型"
                rules={[{ required: true, message: '请选择操作员类型' }]}
              >
                <Select placeholder="选择类型">
                  {operatorTypes.map(type => (
                    <Option key={type.value} value={type.value}>
                      {getOperatorTypeTag(type.value)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="operator_name"
            label="操作人姓名"
            rules={[{ required: true, message: '请输入操作人姓名' }]}
          >
            <Input placeholder="例：王帅" />
          </Form.Item>

          <Form.Item
            name="description"
            label="描述"
          >
            <Input.TextArea
              rows={3}
              placeholder="选填：操作码用途说明"
              maxLength={200}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="状态"
            valuePropName="checked"
          >
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={closeModal}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                {editingCode ? '更新' : '创建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default OperatorCodes;

